import config from "Configurations";

// Public brand demo endpoints (pivony-api api/prospect_demo_routes.py). No JWT:
// these pages are opened by prospects who have no Pivony account yet.
function apiRoot() {
  return (config.BASE_URL || "").replace(/\/+$/, "");
}

function demoUrl(slug, suffix = "") {
  return `${apiRoot()}/feedback/public/prospect-demos/${encodeURIComponent(slug)}${suffix}`;
}

async function send(url, options = {}) {
  try {
    const res = await fetch(url, { credentials: "omit", ...options });
    const body = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, body };
  } catch (error) {
    return { ok: false, status: 0, body: {} };
  }
}

function post(url, payload, headers = {}) {
  return send(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify(payload),
  });
}

export const fetchDemo = (slug) => send(demoUrl(slug));

export const askDemo = (slug, question, channel) => post(demoUrl(slug, "/chat"), { question, channel });

export const fetchDemoPanel = (slug, key) => post(demoUrl(slug, "/panel"), { key });

// Example brands (fictional businesses). Only daily counts are kept: opened, asked, and clicks on
// sign-up / book-a-call, per example and per link label (?ref= on a link Pivony sends).
function exampleUrl(slug = "", suffix = "") {
  const path = slug ? `/${encodeURIComponent(slug)}` : "";
  return `${apiRoot()}/feedback/public/prospect-examples${path}${suffix}`;
}

export const listExamples = (lang) => send(`${exampleUrl()}?lang=${encodeURIComponent(lang || "en")}`);

export const fetchExample = (slug, { ref = "", view = false } = {}) => {
  const params = new URLSearchParams();
  if (ref) params.set("ref", ref);
  if (view) params.set("view", "1");
  const query = params.toString();
  return send(`${exampleUrl(slug)}${query ? `?${query}` : ""}`);
};

// A random id for this browser, so the API can give each visitor 10 answers per example per day.
// It identifies nothing else; the API stores only a keyed hash of it.
const VISITOR_KEY = "sonic-example-visitor";

export function exampleVisitorId() {
  try {
    const saved = window.localStorage.getItem(VISITOR_KEY);
    if (saved && /^[A-Za-z0-9_-]{16,128}$/.test(saved)) return saved;
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    const id = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    window.localStorage.setItem(VISITOR_KEY, id);
    return id;
  } catch (error) {
    return ""; // storage blocked: the API counts the network instead
  }
}

// Pivony staff signed in to the console get a higher limit; everyone else sends no token.
function consoleAuthHeader() {
  try {
    const item = JSON.parse(window.localStorage.getItem("jwt_token") || "null");
    return item && item.value ? { Authorization: `Bearer ${item.value}` } : {};
  } catch (error) {
    return {};
  }
}

// ``auto``: the example's first question, asked when the page opens (not counted as a visitor question).
// ``history`` sends back each earlier answer with the ``sig`` the API gave it; unsigned answers are ignored.
// ``channel``: the phone tab, kept with the conversation for Pivony's ops page.
// A 429 with ``error: "visitor_limit"`` carries ``signup_url``: the visitor has used this example's answers.
export const askExample = (slug, question, history, ref = "", auto = false, channel = "website") =>
  post(
    exampleUrl(slug, "/chat"),
    { question, history, ref, auto, channel, visitor: exampleVisitorId() },
    consoleAuthHeader()
  );

export function trackExample(slug, kind, ref = "") {
  const url = exampleUrl(slug, "/event");
  const body = JSON.stringify({ kind, ref });
  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    return;
  }
  post(url, { kind, ref });
}

export function trackDemo(slug, kind) {
  const url = demoUrl(slug, "/event");
  const body = JSON.stringify({ kind });
  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    return;
  }
  post(url, { kind });
}

// The brand's private panel key, kept for this browser tab only after the link is opened.
const keyName = (slug) => `sonic-demo-panel:${slug}`;

export function rememberPanelKey(slug, key) {
  try {
    window.sessionStorage.setItem(keyName(slug), key);
  } catch (error) {
    // storage blocked: the panel still opens from the link itself
  }
}

export function panelKey(slug) {
  try {
    return window.sessionStorage.getItem(keyName(slug)) || "";
  } catch (error) {
    return "";
  }
}
